import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@libs/zapi-ui/shadcn";
import type { TBlockRender, TTableBlock } from "@/zapi-frontend/shared/types/contentBlocks";
import { parseNumericCell } from "@/zapi-frontend/shared/lib/contentBlocks";
import { refForCell } from "@/zapi-frontend/shared/lib/figureRefs";
import { TableBlock } from "./_TableBlock";
import { FigureTrigger } from "./FigureRefs/FigureTrigger";
import {
  BarRowsLayout,
  CHART_CURSOR_FILL,
  CHART_GRID_STROKE,
  CHART_INK,
  ChartBlockFrame,
  MonoAxisTick,
  ROW_AXIS_HEIGHT,
  ROW_PITCH,
  displayValueFormatter,
  formatChartNumber,
  includeZero,
  tooltipRowLabel
} from "./_ChartBlockFrame";
import { memoType } from "./memoTypography";
import { cn } from "@/zapi-frontend/shared/lib/utils";

const MAX_COLUMN_BARS = 5;
const COLUMN_CHART_HEIGHT = 180;
const COLUMN_AXIS_MIN_WIDTH = 33;
const AXIS_TICK_MARGIN = 8;
const MONO_CHAR_WIDTH = 9;
const AXIS_LABEL_SLACK = 2;
const TICK_HEADROOM = 1.25;

// The axis has to be as wide as its longest tick label or the labels get clipped at the
// left edge; the topmost auto tick can round up past the data, hence the headroom, and the
// slack covers sub-pixel glyph widths when a fallback monospace font is in use.
const columnAxisWidth = (values: number[]) => {
  const bounds = [
    Math.ceil(Math.max(...values, 0) * TICK_HEADROOM),
    Math.floor(Math.min(...values, 0) * TICK_HEADROOM)
  ];
  const chars = Math.max(...bounds.map((bound) => formatChartNumber(bound).length));
  return Math.max(COLUMN_AXIS_MIN_WIDTH, chars * MONO_CHAR_WIDTH + AXIS_TICK_MARGIN + AXIS_LABEL_SLACK);
};
const COLUMN_BAR_SIZE = 20;
const ROW_BAR_SIZE = 18;

type TBarPoint = { index: number; label: string; display: string; value: number; figureKey: string | undefined };

const MIN_BARS = 2;

// Bars need at least two rows to compare. Below that the table says more than a chart would, and
// it holds every row either way — a chart is a suggestion over the data, never a filter on it.
export function BarRowsBlock({ block, render }: { block: TTableBlock; render: TBlockRender }) {
  const valueIndex = render.valueCol ?? 1;
  const data: TBarPoint[] = block.rows
    .map((row, r) => ({
      label: row[0],
      display: row[valueIndex] ?? "",
      value: parseNumericCell(row[valueIndex] ?? ""),
      figureKey: refForCell(block.refs, r, valueIndex)?.key
    }))
    .filter((point): point is Omit<TBarPoint, "index"> => point.value !== null)
    .map((point, index) => ({ ...point, index }));
  if (data.length < MIN_BARS) return <TableBlock block={block} />;

  return (
    <ChartBlockFrame
      label={block.label}
      note={render.note}
      cols={block.cols}
      rows={block.rows}
      refs={block.refs}
      skipped={block.rows.length - data.length}
    >
      {data.length <= MAX_COLUMN_BARS ? (
        <ColumnBars data={data} valueCol={block.cols[valueIndex] ?? ""} />
      ) : (
        <RowBars data={data} valueCol={block.cols[valueIndex] ?? ""} />
      )}
    </ChartBlockFrame>
  );
}

// A handful of values read as columns: the figures sit in a row above the plot and the
// category labels in a row below it, both padded past the axis so they line up with the bars.
function ColumnBars({ data, valueCol }: { data: TBarPoint[]; valueCol: string }) {
  const config: ChartConfig = { value: { label: valueCol, color: CHART_INK } };
  const axisWidth = columnAxisWidth(data.map((point) => point.value));
  const axisOffset = { paddingLeft: axisWidth };

  return (
    <div data-testid="bar-columns">
      <div className={cn("flex text-center", memoType.columnValue)} style={axisOffset}>
        {data.map((point) => (
          <div key={point.index} className="min-w-0 flex-1 px-1">
            <FigureTrigger figureKey={point.figureKey}>{point.display}</FigureTrigger>
          </div>
        ))}
      </div>
      <ChartContainer config={config} className="aspect-auto w-full" style={{ height: COLUMN_CHART_HEIGHT }}>
        <BarChart data={data} margin={{ top: 12, right: 0, bottom: 12, left: 0 }} barCategoryGap={0}>
          <CartesianGrid vertical={false} stroke={CHART_GRID_STROKE} />
          <XAxis dataKey="index" hide />
          <YAxis
            width={axisWidth}
            tickSize={0}
            tickMargin={AXIS_TICK_MARGIN}
            tickLine={false}
            axisLine={false}
            tick={<MonoAxisTick />}
            domain={[includeZero, "auto"]}
          />
          <ChartTooltip
            cursor={{ fill: CHART_CURSOR_FILL }}
            content={<ChartTooltipContent labelFormatter={tooltipRowLabel} formatter={displayValueFormatter} />}
          />
          <Bar
            name={valueCol}
            dataKey="value"
            fill="var(--color-value)"
            barSize={COLUMN_BAR_SIZE}
            isAnimationActive={false}
          />
        </BarChart>
      </ChartContainer>
      <div className={cn("mt-2 flex text-center", memoType.chartLabel)} style={axisOffset}>
        {data.map((point) => (
          <div key={point.index} className="min-w-0 flex-1 px-1">
            {point.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function RowBars({ data, valueCol }: { data: TBarPoint[]; valueCol: string }) {
  const config: ChartConfig = { value: { label: valueCol, color: CHART_INK } };

  return (
    <BarRowsLayout rows={data}>
      <ChartContainer
        config={config}
        className="aspect-auto w-full"
        style={{ height: data.length * ROW_PITCH + ROW_AXIS_HEIGHT }}
      >
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 0 }} barCategoryGap={0}>
          <CartesianGrid horizontal={false} stroke={CHART_GRID_STROKE} />
          <XAxis
            type="number"
            height={ROW_AXIS_HEIGHT}
            interval={0}
            tickLine={false}
            axisLine={false}
            tick={<MonoAxisTick />}
            domain={[includeZero, "auto"]}
          />
          <YAxis type="category" dataKey="index" hide />
          <ChartTooltip
            cursor={{ fill: CHART_CURSOR_FILL }}
            content={<ChartTooltipContent labelFormatter={tooltipRowLabel} formatter={displayValueFormatter} />}
          />
          <Bar
            name={valueCol}
            dataKey="value"
            fill="var(--color-value)"
            barSize={ROW_BAR_SIZE}
            isAnimationActive={false}
          />
        </BarChart>
      </ChartContainer>
    </BarRowsLayout>
  );
}
